import { ref } from 'vue'
import { getAuth, signInWithPopup, GoogleAuthProvider } from 'firebase/auth'
import axios from 'axios'

/**
 * Invite-only sign-up composable. Sign-up is by invitation: these calls create
 * the Firebase account only, and the invite flow handles org/project membership.
 *
 * For existing user sign-in, use useAuth.ts instead.
 */

export function useSignUp() {
  const auth = getAuth()

  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Sign up for an invite — creates Firebase account only.
   * No org or project is created; the invite flow handles membership.
   */
  const signUpForInvite = async (params: {
    email: string
    password: string
    displayName: string
  }): Promise<{ message: string } | null> => {
    try {
      isLoading.value = true
      error.value = null

      const response = await axios.post<{ message: string }>('/api/3/sign-up/invite', {
        email: params.email,
        password: params.password,
        display_name: params.displayName,
      })

      return response.data
    } catch (err: any) {
      console.error('[useSignUp] Invite sign-up error:', err)
      const detail = err.response?.data?.detail
      if (typeof detail === 'string') {
        error.value = detail
      } else if (typeof detail === 'object' && detail?.message) {
        error.value = detail.message
      } else {
        error.value = err.message || 'Failed to sign up'
      }
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Sign up via Google for an invited user — no org or project created.
   */
  const signUpWithGoogleForInvite = async (): Promise<{ message: string; token: string } | null> => {
    try {
      isLoading.value = true
      error.value = null

      const provider = new GoogleAuthProvider()
      provider.setCustomParameters({ prompt: 'select_account' })
      const result = await signInWithPopup(auth, provider)
      const googleToken = await result.user.getIdToken()

      const response = await axios.post<{ message: string }>('/api/3/sign-up/google-invite', {
        google_token: googleToken,
      })

      return { ...response.data, token: googleToken }
    } catch (err: any) {
      if (err.code === 'auth/popup-closed-by-user') return null
      console.error('[useSignUp] Google invite sign-up error:', err)
      const detail = err.response?.data?.detail
      if (typeof detail === 'string') {
        error.value = detail
      } else if (typeof detail === 'object' && detail?.message) {
        error.value = detail.message
      } else {
        error.value = err.message || 'Failed to sign up with Google'
      }
      return null
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Clear error message
   */
  const clearError = () => {
    error.value = null
  }

  return {
    // State
    isLoading,
    error,

    // Methods
    signUpWithGoogleForInvite,
    signUpForInvite,
    clearError
  }
}
